import React, { useState } from 'react';
import { supabase } from '../supabase';
import { PrimaryBtn, SecondaryBtn } from './systemKit';

const COOLDOWN_DAYS = 30;

/**
 * Dismiss-alert modal.
 *
 * Copy is intentionally honest about the 30-day cooldown — users are not
 * permanently saying "ignore this", they're saying "I've seen it, give me
 * a break." If the signal worsens during the window the cooldown logic in
 * shrinkflation.js will refire (re-fire is allowed when the new |change_pct|
 * exceeds the cooldown's recorded value).
 */
export default function DismissAlertModal({ alert, onClose, onDismissed }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleDismiss() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || null;
      const nowIso = new Date().toISOString();
      const cooldownIso = new Date(Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000).toISOString();
      await supabase.from('shrinkflation_alerts')
        .update({
          status: 'dismissed',
          dismissed_at: nowIso,
          dismissed_by: email,
          dismiss_reason: reason || null,
          cooled_down_until: cooldownIso,
          updated_at: nowIso,
        })
        .eq('id', alert.id);
      onDismissed?.();
    } catch (e) {
      console.error('DismissAlertModal: dismiss failed', e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="rounded-lg p-6 w-full max-w-sm mx-4"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        <div className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Dismiss this alert?
        </div>
        <div className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          We'll stop watching this alert for {COOLDOWN_DAYS} days. If it changes again, we'll let you know.
        </div>

        <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g., Confirmed with vendor — temporary supply issue"
          rows={3}
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none mb-4"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)', resize: 'vertical' }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
        />

        <div className="flex justify-end gap-2">
          <SecondaryBtn onClick={onClose} disabled={submitting}>Cancel</SecondaryBtn>
          <PrimaryBtn onClick={handleDismiss} disabled={submitting}>
            {submitting ? 'Dismissing…' : 'Dismiss'}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
